import React from 'react';
import { Link } from 'react-router-dom';
import { Recycle, CheckCircle, FileText, ArrowRight, Flame, Leaf } from 'lucide-react';

const SoilRemediation = () => {
  const contaminants = [
    { name: 'PFAS (Per- and Polyfluoroalkyl Substances)', description: 'Persistent organic pollutants requiring specialized treatment' },
    { name: 'Petroleum Hydrocarbons', description: 'Oil and fuel contamination from spills and leaks' },
    { name: 'Heavy Metals', description: 'Lead, arsenic, mercury, and other toxic metals' },
    { name: 'Volatile Organic Compounds (VOCs)', description: 'Industrial solvents and chemicals' },
  ];

  const remediationMethods = [
    {
      method: 'Thermal Desorption',
      description: 'Heating contaminated soil to volatilize and separate contaminants',
      applications: 'Effective for petroleum hydrocarbons and VOCs',
    },
    {
      method: 'Thermal Incineration',
      description: 'High-temperature combustion to destroy organic contaminants',
      applications: 'Suitable for heavily contaminated soils and hazardous materials',
    },
    {
      method: 'Bioremediation',
      description: 'Using microorganisms to break down contaminants',
      applications: 'Petroleum products and organic compounds',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="soil-remediation-page">
      {/* Hero Section */}
      <section
        className="hero-section relative text-white overflow-hidden"
        style={{
          height: '60vh',
          maxHeight: '720px',
          minHeight: '600px',
          padding: '5rem 0'
        }}
      >
        {/* Responsive Hero Image Container */}
        <div className="absolute inset-0 z-0">
          <style>{`
            .hero-section picture {
              width: 100%;
              height: 100%;
              display: block;
            }
            
            .hero-section .hero-image,
            .hero-section picture img {
              width: 100% !important;
              height: 100% !important;
              object-fit: cover !important;
              object-position: center bottom !important;
              display: block !important;
            }
            
            @media (max-width: 900px) {
              .hero-section {
                height: auto !important;
                max-height: none !important;
                min-height: 420px !important;
                padding: 3rem 0 !important;
              }
              .hero-section picture img {
                object-position: center center !important;
              }
            }
          `}</style>
          
          <picture>
            {/* Desktop */}
            <source
              media="(min-width: 1280px)"
              srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/wpz70p76_soil_hero_desktop_2560x1200.webp"
              type="image/webp" />

            {/* Tablet */}
            <source
              media="(min-width: 768px)"
              srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/9ptqpwfe_soil_hero_tablet_1536x900.webp"
              type="image/webp" />

            {/* Mobile Horizontal */}
            <source
              media="(min-width: 480px)"
              srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/98da0l5h_soil_hero_mobile_horizontal_960x640.webp"
              type="image/webp" />

            {/* Mobile Vertical (fallback for portrait) */}
            <img
              src="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/3weagjxn_soil_hero_mobile_vertical_960x1200.webp"
              alt="Soil remediation operations with thermal oxidizer processing contaminated soil at an industrial cleanup site"
              loading="eager"
              fetchpriority="high"
              decoding="async"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center bottom' }}
              className="w-full h-full" />
          </picture>
          
          {/* Dark Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/50 to-transparent"></div>
        </div>
        
        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10 h-full flex items-center">
          <div className="max-w-3xl">
            <div className="flex items-center mb-6">
              <Recycle className="w-16 h-16 mr-4" />
              <div>
                <h1 className="text-4xl md:text-5xl font-bold">
                  Soil Remediation & Thermal Incinerators
                </h1>
                <p className="text-xl mt-2">Environmental Restoration & Air Quality Compliance</p>
              </div>
            </div>
            <p className="text-xl md:text-2xl max-w-3xl">
              Expert soil remediation services aimed at restoring contaminated land and promoting ecosystem health.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Restoring Alaska's Contaminated Sites</h2>
            <p className="text-lg text-gray-600 mb-4 leading-relaxed">
              Raven Environmental Consulting excels in soil remediation aimed at restoring contaminated land to promote ecosystem health and improve land value. Specializing in PFAS and other pollutants, we utilize advanced technologies and methodologies to address soil contamination effectively.
            </p>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              Our comprehensive approach includes thermal treatment technologies such as thermal desorption and incineration, which are particularly effective for sites contaminated with persistent organic pollutants and petroleum products common in Alaska's industrial operations.
            </p>
            
            <div className="bg-emerald-50 p-6 rounded-lg border-l-4 border-emerald-600 flex items-start">
              <Leaf className="w-8 h-8 text-emerald-600 mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-gray-900 mb-3">Environmental Restoration Mission</h3>
                <p className="text-gray-700">
                  Our goal is to restore contaminated land to productive use while ensuring compliance with all environmental regulations and protecting Alaska's unique ecosystems.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contaminants */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Common Contaminants We Address</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {contaminants.map((contaminant, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-emerald-600">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{contaminant.name}</h3>
                <p className="text-gray-600">{contaminant.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Remediation Methods */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Remediation Technologies</h2>
          <div className="space-y-6 max-w-5xl mx-auto">
            {remediationMethods.map((method, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-lg shadow-lg">
                <div className="flex items-start mb-4">
                  <Flame className="w-8 h-8 text-emerald-600 mr-4 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{method.method}</h3>
                    <p className="text-gray-600 mb-3">{method.description}</p>
                    <div className="bg-white p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-2">Applications:</h4>
                      <p className="text-gray-700">{method.applications}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regulatory Requirements */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Federal Air Quality Requirements for Thermal Treatment</h2>
          <div className="space-y-8 max-w-5xl mx-auto">
            {/* NSPS */}
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">40 CFR 60 - New Source Performance Standards</h3>
              <p className="text-gray-600 mb-4">Thermal treatment facilities must comply with NSPS requirements:</p>
              <div className="space-y-3">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Subpart O - Sewage Sludge Incinerators</h4>
                  <p className="text-sm text-gray-600">Particulate matter emission limits and monitoring requirements</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Subpart Ec - Hospital/Medical/Infectious Waste Incinerators</h4>
                  <p className="text-sm text-gray-600">Emission standards for certain thermal treatment operations</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Subpart IIII - Combustion Engines</h4>
                  <p className="text-sm text-gray-600">Standards for auxiliary engines and power generation</p>
                </div>
              </div>
            </div>

            {/* NESHAP */}
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">40 CFR 63 - NESHAP Requirements</h3>
              <p className="text-gray-600 mb-4">Hazardous Air Pollutant standards for thermal treatment:</p>
              <div className="space-y-3">
                <div className="flex items-start bg-gray-50 p-4 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Subpart EEE - Hazardous Waste Combustors</h4>
                    <p className="text-sm text-gray-600">Comprehensive standards for combustion of hazardous materials</p>
                  </div>
                </div>
                <div className="flex items-start bg-gray-50 p-4 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Stack Testing Requirements</h4>
                    <p className="text-sm text-gray-600">Periodic emissions testing to verify compliance with standards</p>
                  </div>
                </div>
                <div className="flex items-start bg-gray-50 p-4 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Continuous Monitoring</h4>
                    <p className="text-sm text-gray-600">CEMS and operational parameter monitoring requirements</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Alaska Requirements */}
            <div className="bg-white p-8 rounded-lg shadow-lg border-l-4 border-emerald-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Alaska DEC Requirements</h3>
              <p className="text-gray-600 mb-4">
                State-specific requirements for soil remediation and thermal treatment:
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Contaminated Sites Program coordination</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Air quality permits for thermal treatment facilities</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Soil cleanup levels and remediation goals</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Recordkeeping and reporting requirements</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Benefits of Professional Soil Remediation</h2>
            <div className="bg-gray-50 p-8 rounded-lg shadow-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start bg-white p-4 rounded-lg">
                  <Leaf className="w-6 h-6 text-emerald-600 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Ecosystem Restoration</h4>
                    <p className="text-sm text-gray-600">Return land to productive use and restore natural habitats</p>
                  </div>
                </div>
                <div className="flex items-start bg-white p-4 rounded-lg">
                  <Leaf className="w-6 h-6 text-emerald-600 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Increased Land Value</h4>
                    <p className="text-sm text-gray-600">Cleaned sites have higher market value and development potential</p>
                  </div>
                </div>
                <div className="flex items-start bg-white p-4 rounded-lg">
                  <Leaf className="w-6 h-6 text-emerald-600 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Regulatory Compliance</h4>
                    <p className="text-sm text-gray-600">Meet all federal and state environmental requirements</p>
                  </div>
                </div>
                <div className="flex items-start bg-white p-4 rounded-lg">
                  <Leaf className="w-6 h-6 text-emerald-600 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Public Health Protection</h4>
                    <p className="text-sm text-gray-600">Remove contaminants that pose risks to community health</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-emerald-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Transform Contaminated Sites into Productive Land</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Our expertise in soil remediation and thermal treatment ensures effective cleanup while maintaining environmental compliance.
          </p>
          <Link
            to="/sectors/soil-remediation/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-emerald-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
          >
            Start Your Remediation Project
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SoilRemediation;
